/**
 * Pure derivations for the container `max_tracked_record_bytes` pre-flight
 * (operator-task-fe-set-max-tracked-size-overlimit-check-modal).
 *
 * Extracted out of `ResourceDetails.tsx` / `MaxTrackedSizeOverLimitDialog.tsx`
 * so the tricky bits (empty -> no-limit, kB<->bytes rounding, when to run the
 * pre-flight, raise-to-fit rounding) are unit-testable in isolation without
 * having to render the container form or mock the REST/redux side effects.
 * Behaviour is identical to the inline logic these replace.
 */

/**
 * Convert the kB-entered `max_tracked_record_bytes` form value to the bytes the
 * backend column stores. An empty string or null means "no limit" -> null.
 * (1 kB = 1024 bytes; the value is rounded to whole bytes.)
 */
export const kbFieldToBytes = (raw: unknown): number | null => {
    if (raw === "" || raw == null) {
        return null;
    }
    return Math.round(Number(raw) * 1024);
};

/**
 * Whether setting this value should trigger the over-limit pre-flight check.
 * Only when the record is a container (project/bundle/workspace), the limit
 * field actually changed in this submit, and a concrete (non-null) limit is
 * being set — clearing the limit (null) never needs a pre-flight.
 */
export const shouldPreflightMaxTrackedBytes = (
    isContainer: boolean,
    changedFields: readonly string[],
    candidateBytes: number | null
): boolean =>
    isContainer &&
    changedFields.includes("max_tracked_record_bytes") &&
    candidateBytes != null;

/**
 * The "raise the limit so everything fits" value, in bytes: the largest
 * over-limit record rounded UP to the next whole kB (so the field, which is
 * entered in kB, shows a clean number and the biggest record is included).
 * Falls back to the candidate limit when the list is empty.
 */
export const computeRaiseToFitBytes = (
    records: ReadonlyArray<{ size_bytes: number }>,
    candidateBytes: number
): number => {
    const maxBytes = records.length
        ? Math.max(...records.map((record) => record.size_bytes))
        : candidateBytes;
    return Math.ceil(maxBytes / 1024) * 1024;
};

/** kB label for a byte count (matches the field's kB display). */
export const bytesToKb = (bytes: number): number => Math.round(bytes / 1024);
